/**
 * O CONTEÚDO dos tours. Separado do motor de propósito.
 *
 * Mexer num texto não pode exigir entender posicionamento, e esta é a parte
 * que mais vai mudar. Quem vier ajustar palavra encosta só aqui.
 *
 * ## O contrato de um passo
 *
 * | campo | o que é |
 * |---|---|
 * | `id` | estável; é o que vai no log e no teste de contrato |
 * | `alvo` | o `data-tour` do elemento. **Ausente = passo centralizado** |
 * | `titulo` | uma linha |
 * | `texto` | uma ou duas frases. Mais que isso ninguém lê num balão |
 * | `lado` | preferência, não ordem — o motor vira se não couber |
 * | `rota` | leva a tela até lá antes de medir. **Passo que muda de aba** |
 * | `quando` | função opcional: o passo só aparece se devolver `true` |
 *
 * ## ⚠ Toda âncora aqui precisa existir no JSX
 *
 * Passo cujo alvo não existe é **pulado em silêncio**, que é o comportamento
 * certo (ver regra 3 do estudo) e exatamente por isso esconde o estrago quando
 * alguém renomeia um componente. Quem guarda isso é
 * `backend/tests/tour-ancoras.test.js`, que cruza este arquivo com o JSX.
 *
 * ## ⚠ `versao` só sobe quando o conteúdo mudou DE VERDADE
 *
 * Subir reapresenta o tour a quem já viu. Corrigir uma vírgula não justifica
 * interromper de novo quem já aprendeu.
 */

/**
 * Tour 1 — para todo mundo.
 *
 * Enredo: *o que existe, o que você pode abrir, e o que fazer quando não pode.*
 *
 * Começa onde a pessoa cai depois do login, e termina no canal que responde.
 */
export const TOUR_INICIAL = {
  id: 'inicial',
  versao: 2,
  papel: 'todos',
  nome: 'Conhecer o AI Hub',
  resumo: 'O que existe aqui, o que você abre, e o que fazer quando algo não abre.',
  rota: '/',
  passos: [
    {
      id: 'boas-vindas',
      titulo: 'Bem-vindo ao AI Hub',
      texto:
        'Este é o catálogo de agentes e sistemas de IA da Iguatemi. Em um minuto você sabe o que existe, como abrir, e o que fazer quando algo não abre para você.',
    },
    {
      id: 'secoes',
      alvo: 'nav-secoes',
      lado: 'abaixo',
      titulo: 'Três acervos e um acompanhamento',
      texto:
        'Agentes conversam com você. Sistemas são as plataformas onde o trabalho acontece. Artefatos são páginas que as pessoas daqui publicam. Depois da divisória, Solicitações — que não é acervo, é o andamento dos seus pedidos.',
    },
    {
      id: 'cartao',
      alvo: 'catalogo-cartao',
      lado: 'direita',
      titulo: 'Cada cartão é um item',
      texto:
        'Nome, área responsável e para que serve. Clique no cartão para ver o detalhe e abrir — os atalhos do canto levam direto ao destino, quando há mais de um.',
      quando: ({ temCartao }) => temCartao,
    },
    {
      id: 'estados',
      alvo: 'catalogo-cartao',
      lado: 'direita',
      titulo: 'A pastilha diz o que você pode fazer',
      texto:
        'Liberado abre na hora. Solicitar acesso quer dizer que este item é controlado por um grupo do seu login corporativo, e você ainda não está nele.',
      quando: ({ temCartao }) => temCartao,
    },
    {
      id: 'solicitar',
      alvo: 'catalogo-cartao',
      lado: 'direita',
      titulo: 'Pedir acesso abre um chamado de verdade',
      texto:
        'Não é um botão que libera na hora: vira um chamado no ServiceNow, com aprovador responsável pelo item. A resposta volta para cá.',
      quando: ({ temItemParaSolicitar }) => temItemParaSolicitar,
    },
    {
      id: 'acompanhar',
      alvo: 'nav-solicitacoes',
      lado: 'abaixo',
      rota: '/solicitacoes',
      titulo: 'Aqui você acompanha o que pediu',
      texto:
        'Cada pedido com o número do chamado e em que pé está. Nada se perde entre o clique e a resposta.',
    },
    {
      id: 'sino',
      alvo: 'sino',
      lado: 'esquerda',
      rota: '/',
      titulo: 'É aqui que a resposta chega',
      texto:
        'Aprovação, recusa, e quando alguém compartilha um artefato com você. Clicar na notificação leva direto ao que ela fala.',
    },
    {
      id: 'feedback',
      alvo: 'feedback',
      lado: 'acima',
      titulo: 'Dúvida, ideia ou problema',
      texto:
        'A equipe de Inovação e IA responde por aqui, e a conversa fica guardada. É o canal certo quando algo não faz sentido — inclusive sobre esta tela.',
    },
    {
      id: 'fim',
      titulo: 'É isso',
      texto:
        'Os tours ficam no menu do seu nome, no canto superior direito. Repita quando quiser, ou chame o que fala de publicar artefatos.',
    },
  ],
};

/**
 * Tour 3 — só para administrador.
 *
 * Enredo: *quem entra, o que eles veem, e o que você faz quando algo sai do
 * lugar.*
 *
 * ## Este é o tour que atravessa telas
 *
 * Cada passo declara a `rota` com a aba dele, e o motor navega antes de medir.
 * Funciona porque a aba da administração passou a sair da URL em 21/10 — antes
 * era estado semeado uma vez, e navegar não mexia em nada.
 *
 * **Troca de aba sozinho, de propósito.** A alternativa era pedir o clique a
 * cada passo, e em nove passos isso vira tarefa: quem está aprendendo gasta a
 * atenção em obedecer instrução de navegação em vez de ler o que a aba faz. O
 * preço é a tela mexer sem a pessoa pedir — pago com a contagem "N de 9" e o
 * *Sair do tour* sempre à vista.
 */
export const TOUR_ADMIN = {
  id: 'admin',
  versao: 2,
  papel: 'admin',
  nome: 'Administrar o AI Hub',
  resumo: 'Quem entra, o que eles veem, e o que fazer quando algo sai do lugar.',
  rota: '/admin?aba=requests',
  passos: [
    {
      id: 'familias',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=requests',
      titulo: 'Oito abas, três famílias',
      texto:
        'As divisórias marcam três grupos: quem entra e o que pode abrir; o que a plataforma mostra; e a conversa com quem usa. Este tour passa por todas.',
    },
    {
      id: 'solicitacoes',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=requests',
      titulo: 'Solicitações: a decisão vai para o ServiceNow',
      texto:
        'Aprovar ou recusar aqui escreve no chamado de lá, e é de lá que o acesso sai — uma porta só, nunca duas. Ninguém decide a própria solicitação: outro administrador decide.',
    },
    {
      id: 'usuarios',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=users',
      titulo: 'Usuários: quem entrou, e por que consegue',
      texto:
        'Quem já entrou, quando, e quem é administrador. O botão Ver grupos abre os grupos do Entra daquela pessoa e o que cada um libera — é a resposta de "por que fulano não consegue abrir o agente X".',
    },
    {
      id: 'acessos',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=access',
      titulo: 'Acessos: a liberação manual',
      texto:
        'Liberação manual, pessoa por pessoa, sem passar pelo AD. Serve para exceção e para teste; o caminho que escala é o grupo, na próxima aba.',
    },
    {
      id: 'grupos',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=grupos',
      titulo: 'Grupos: é aqui que o acesso automático acontece',
      texto:
        'Ligar um grupo do AD a um agente faz esse agente abrir só para quem está no grupo — e abrir sozinho, sem ninguém pedir. É a ação mais poderosa deste painel, e fica registrada com autor e hora.',
    },
    {
      id: 'alcance',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=grupos',
      titulo: 'Veja o alcance antes de confirmar',
      texto:
        'Antes de confirmar, a janela diz quantas pessoas o grupo tem no Entra e quantas já entraram no AI Hub. Clique nos rostos da lista para ver quem são, uma a uma.',
    },
    {
      id: 'catalogo',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=catalog',
      titulo: 'Catálogo: o que aparece para as pessoas',
      texto:
        'Nome, descrição, área responsável e por onde o item abre. O padrão de acesso não se edita neste formulário: ele muda sozinho quando o item recebe o primeiro grupo.',
    },
    {
      id: 'artefatos',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=sites',
      titulo: 'Artefatos: despublicar não é apagar',
      texto:
        'Despublicar fecha o alcance e devolve os arquivos a quem publicou. Apagar é a outra porta, e as duas exigem motivo. Abrir o conteúdo de outra pessoa fica no registro — inclusive o seu.',
    },
    {
      id: 'conversa',
      alvo: 'admin-abas',
      lado: 'abaixo',
      rota: '/admin?aba=feedback',
      titulo: 'Mensagens e Conversas',
      texto:
        'Mensagens é o canal de volta com quem usa a plataforma, e a conversa inteira fica guardada. Conversas é o histórico de uso dos agentes. Este tour fica no menu do seu nome.',
    },
  ],
};

/**
 * Tour 2 — para quem publica artefato.
 *
 * Enredo: *você fez uma coisa. Agora: quem vê, e como ela chega até eles.*
 *
 * ## Por que ele mora inteiro na LISTA, e não no detalhe
 *
 * O plano original levava os três últimos passos para a tela de um artefato.
 * Não dá: `/artefatos/:id` exige um identificador, e o tour não tem como saber
 * qual — navegar para o artefato de alguém seria escolher por essa pessoa, e
 * para quem ainda não publicou nada não haveria destino nenhum.
 *
 * A lista já mostra o que importa — a pastilha de visibilidade, os rostos de
 * com quem está compartilhado, as duas formas de criar. O que só existe no
 * detalhe é **apontado** a partir daqui, no último passo, em vez de visitado.
 *
 * ## Os passos somem quando a lista está vazia
 *
 * Quem nunca publicou é justamente quem mais precisa deste tour, e para essa
 * pessoa não há cartão nenhum na tela. Os passos que falam de cartão têm
 * `quando`, e o tour encolhe de seis para três — continuando a ensinar o que
 * ela pode fazer, sem apontar para o vazio.
 */
export const TOUR_ARTEFATOS = {
  id: 'artefatos',
  versao: 2,
  papel: 'todos',
  nome: 'Publicar um artefato',
  resumo: 'O que é um artefato, quem vê, e como compartilhar.',
  rota: '/artefatos',
  passos: [
    {
      id: 'o-que-e',
      rota: '/artefatos',
      titulo: 'Artefato é uma página hospedada aqui',
      texto:
        'Um painel, um relatório, uma demonstração. Você sobe o HTML, ele ganha endereço próprio no AI Hub, e abre para quem você escolher.',
    },
    {
      id: 'criar',
      alvo: 'artefatos-criar',
      lado: 'abaixo',
      rota: '/artefatos',
      titulo: 'Dois caminhos para publicar',
      texto:
        'Novo artefato envia os arquivos desta tela. Publicar do Claude liga o AI Hub à sua conversa — configura uma vez por computador, e depois a página sobe direto de lá.',
    },
    {
      id: 'cartao',
      alvo: 'artefatos-cartao',
      lado: 'direita',
      rota: '/artefatos',
      titulo: 'A miniatura é a página de verdade',
      texto:
        'Não é uma captura de tela: é o seu artefato rodando dentro do cartão. O que aparece aqui é o que as pessoas veem ao abrir.',
      quando: ({ temArtefato }) => temArtefato,
    },
    {
      id: 'visibilidade',
      alvo: 'artefatos-visibilidade',
      lado: 'abaixo',
      rota: '/artefatos',
      titulo: 'Três alcances, não dois',
      texto:
        'Privado é só seu. Compartilhado abre para as pessoas que você listar, uma a uma. Todo o AI Hub abre para qualquer pessoa da Iguatemi que tiver o endereço — nunca para fora dela.',
      quando: ({ temArtefato }) => temArtefato,
    },
    {
      id: 'rostos',
      alvo: 'artefatos-cartao',
      lado: 'direita',
      titulo: 'Quem recebeu aparece no cartão',
      texto:
        'Os rostos mostram com quem o artefato está compartilhado. Dá para compartilhar pelo e-mail com quem ainda nem entrou no AI Hub: o acesso já fica guardado e passa a valer no primeiro login dessa pessoa.',
      quando: ({ temArtefato }) => temArtefato,
    },
    {
      id: 'abrir',
      alvo: 'artefatos-cartao',
      lado: 'direita',
      titulo: 'O resto fica dentro do artefato',
      texto:
        'Abra um artefato para trocar o nome, escolher quem vê e copiar o endereço. Quem recebe esse endereço vê, no topo, quem publicou e com quem a página está compartilhada.',
      quando: ({ temArtefato }) => temArtefato,
    },
    {
      id: 'fim',
      titulo: 'Pronto',
      texto:
        'Este tour fica no menu do seu nome, junto dos outros. E dentro de cada artefato há um "?" ao lado de Quem pode ver, para a dúvida que aparecer na hora.',
    },
  ],
};

/* ── micro-tours ─────────────────────────────────────────────────────────── */

/**
 * Os tours de uma pergunta só, abertos pelo "?" ao lado do controle.
 *
 * ## O que os separa dos outros três
 *
 * `contextual: true` — não aparecem no menu, e **não têm `rota`**. Começam
 * onde a pessoa já está, dentro de uma janela que ela abriu. Dar rota a eles
 * seria inventar um caminho: janela não é endereço, e o motor navega por
 * endereço.
 *
 * ## Por que estes dois, e não os quatro do plano
 *
 * São as perguntas que a plataforma gera **com uma decisão pela frente**:
 * publicar e não saber quem vai ver; ligar um grupo e não saber quantos isso
 * libera. As outras duas do estudo — padrão de acesso e conector — não ganharam
 * micro-tour porque não têm âncora própria na tela: o padrão não é editável e
 * não aparece em campo nenhum do editor, e inventar um lugar para pendurar o
 * "?" seria desenhar a interface em volta da ajuda, e não o contrário.
 */
export const MICRO_VISIBILIDADE = {
  id: 'visibilidade',
  versao: 1,
  papel: 'todos',
  contextual: true,
  nome: 'Quem pode ver este artefato',
  resumo: 'A diferença entre privado, compartilhado e todo o AI Hub.',
  passos: [
    {
      id: 'tres-alcances',
      alvo: 'artefato-visibilidade',
      lado: 'abaixo',
      titulo: 'Privado, e as duas formas de abrir',
      texto:
        'Privado é só você. Todo o AI Hub abre para qualquer pessoa da Iguatemi que tiver o endereço: ninguém de fora entra, e ninguém de dentro fica de fora.',
    },
    {
      id: 'compartilhado',
      alvo: 'artefato-visibilidade',
      lado: 'abaixo',
      titulo: 'Compartilhado é o meio-termo, e não é um botão',
      texto:
        'Não há botão de Compartilhado: ele aparece sozinho quando você adiciona a primeira pessoa à lista, e some quando você tira a última. Vale também para quem ainda nem entrou no AI Hub, pelo e-mail.',
    },
  ],
};

export const MICRO_ALCANCE = {
  id: 'alcance',
  versao: 1,
  papel: 'admin',
  contextual: true,
  nome: 'Quantas pessoas este grupo libera',
  resumo: 'Os dois números, e por que eles são diferentes.',
  passos: [
    {
      id: 'dois-numeros',
      alvo: 'grupo-alcance',
      lado: 'abaixo',
      titulo: 'São dois números, e contam coisas diferentes',
      texto:
        'O primeiro é o tamanho do grupo no Entra — todo mundo que vai receber o agente. O segundo é quantas dessas pessoas já usaram o AI Hub alguma vez.',
    },
    {
      id: 'as-que-faltam',
      alvo: 'grupo-alcance',
      lado: 'abaixo',
      titulo: 'A diferença entre os dois não é erro',
      texto:
        'A diferença são as pessoas do grupo que ainda não usaram a plataforma. Elas recebem o agente no dia em que entrarem, sem pedir nada — por isso quem decide é o número do Entra, e não o daqui.',
    },
    {
      id: 'nao-sei',
      alvo: 'grupo-alcance',
      lado: 'abaixo',
      titulo: 'Se o número do Entra não aparecer',
      texto:
        'Quer dizer que não deu para consultar o diretório agora — nunca que o grupo esteja vazio. O alcance real fica desconhecido, e vale conferir antes de confirmar o vínculo.',
    },
  ],
};

/**
 * Todos os tours.
 *
 * Os três primeiros aparecem no menu, nesta ordem — do geral ao
 * especializado. Os contextuais vivem no "?" ao lado do controle de que
 * falam, e `disponiveis` os exclui da lista.
 */
export const TOURS = [TOUR_INICIAL, TOUR_ARTEFATOS, TOUR_ADMIN, MICRO_VISIBILIDADE, MICRO_ALCANCE];

/** Um tour pelo identificador. */
export const tourPorId = (id) => TOURS.find((t) => t.id === id) ?? null;

/**
 * Os passos que fazem sentido para esta pessoa, neste momento.
 *
 * O filtro acontece **uma vez, na abertura**, e não a cada passo: a contagem de
 * "passo 3 de 6" não pode mudar embaixo de quem está lendo.
 */
export function passosDe(tour, contexto = {}) {
  return tour.passos.filter((p) => (p.quando ? Boolean(p.quando(contexto)) : true));
}

export default {
  TOURS,
  TOUR_INICIAL,
  TOUR_ARTEFATOS,
  TOUR_ADMIN,
  MICRO_VISIBILIDADE,
  MICRO_ALCANCE,
  tourPorId,
  passosDe,
};
